import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../auth/ApiProvider';
import { useI18n } from '../i18n/I18nProvider';
import { useLimits } from '../hooks/LimitsProvider';
import { Badge } from './primitives';

/** How many notifications the short list under the bell shows. */
const LATEST_COUNT = 5;

/**
 * The bell in the header: the unread count, and a short list of the latest with a way to the full centre
 * (decision 2026-09-23 #25).
 *
 * The count is its own small request at the configured list interval (`polling.listMs`), paused while the
 * tab is hidden. Its query key
 * sits under `['notifications']`, so marking something read in the centre refreshes the bell at once.
 */
export function NotificationBell({ to }: { to: string }) {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  const { polling } = useLimits();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    refetchInterval: polling.listMs,
  });

  const latest = useQuery({
    queryKey: ['notifications', 'latest'],
    queryFn: () => api.notifications.list({ pageSize: LATEST_COUNT }),
    enabled: open,
  });

  // Closes on a click anywhere else, and on Escape, like any menu.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = unread.data?.count ?? 0;

  return (
    <div className="dg-bell" ref={container}>
      <button
        type="button"
        className="dg-button dg-button--ghost"
        aria-expanded={open}
        aria-label={count > 0 ? t('notifications.bell.unread', { count }) : t('notifications.bell')}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">🔔</span>
        {count > 0 && <Badge tone="count">{count}</Badge>}
      </button>

      {open && (
        <div className="dg-bell__menu" role="region" aria-label={t('notifications.bell')}>
          {latest.data?.items.length === 0 && <p className="dg-muted dg-text-sm">{t('notifications.empty')}</p>}
          {latest.data?.items.map((item) => (
            <p key={item.id} className={`dg-bell__item${item.readAt ? '' : ' dg-bell__item--unread'}`}>
              <span>{item.subject ?? item.body}</span>
              <span className="dg-muted dg-text-sm">{formatDateTime(item.createdAt)}</span>
            </p>
          ))}
          <Link to={to} className="dg-button dg-button--secondary" onClick={() => setOpen(false)}>
            {t('notifications.seeAll')}
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * How many conversations have unread messages, for the badge on "Messages" (decision 2026-09-23 #25).
 *
 * Its own small count from the server, across every conversation. Counting the list's first page missed
 * any unread thread past it.
 */
export function useUnreadConversations(enabled: boolean): number {
  const api = useApi();
  const { polling } = useLimits();
  // Under ['conversations'], so reading a thread, which invalidates that key, refreshes the badge too.
  const unread = useQuery({
    queryKey: ['conversations', 'unread-count'],
    queryFn: () => api.conversations.unreadCount(),
    refetchInterval: polling.listMs,
    enabled,
  });
  return unread.data?.count ?? 0;
}
